import { Behaviours, createProvisioningPageExtensions, Prompts, TargetTabs } from '@atlassiansox/cross-flow-extensions';
import { createStartExtensions, Editions } from '@atlassiansox/cross-flow-extensions/start';
import { CrossFlowConsumer, Journeys, TargetType } from '@atlassiansox/cross-flow-support';
import { default as StartCrossFlowProvider } from '@atlassiansox/cross-flow-support/start';
import React, { ComponentType, useCallback, useMemo } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';

import { AnalyticsConsumerProps, withAnalytics } from '../../../hoc/withAnalytics';
import { useFabricAnalyticsListenerAdapter } from '../../../hooks/useFabricAnalyticsListener';
import { getParams, stripParams } from '../../../routing';
import { getContainersPlugin } from '../utils/get-containers';

interface CrossFlowWACInitParams {
  billingMode?: 'legacy';
  edition?: Editions;
  migrationSourceUuid?: string;
  sourceComponent?: string;
  sourceContext?: string;
  startTrialCloudId?: string;
  startTrialProduct?: TargetType;
  targetTab?: TargetTabs;
  templateId?: string;
}

function getCrossFlowParams(): CrossFlowWACInitParams {
  return getParams(
    'startTrialCloudId',
    'startTrialProduct',
    'edition',
    'migrationSourceUuid',
    'billingMode',
    'sourceContext',
    'targetTab',
    'sourceComponent',
    'templateId',
  );
}

function stripCrossFlowParams() {
  return stripParams(
    'startTrialCloudId',
    'startTrialProduct',
    /* Even though `startTrialInstance` is not accepted by cross flow any more it may still be coming from various
       external sources */
    'startTrialInstance',
    'edition',
    'migrationSourceUuid',
    'billingMode',
    'sourceContext',
    'targetTab',
    'sourceComponent',
    'templateId',
  );
}

/**
 * "WAC via Start" specific implementation of cross-flow.
 *
 * Will bring up the Cross Flow UI overlay when a startTrialProduct is present in the query string.
 *
 * Should be mounted as a sibling of Cross Flow Provider and won't impact other touch points.
 */
const CrossFlowWAC: ComponentType<InjectedIntlProps & AnalyticsConsumerProps> = props => {
  const { intl } = props;
  const {
    startTrialCloudId,
    startTrialProduct,
    edition,
    migrationSourceUuid,
    billingMode,
    sourceContext,
    targetTab,
    sourceComponent,
    templateId,
  } = getCrossFlowParams();

  const onComplete = useCallback(() => stripCrossFlowParams(), []);

  const fabricAnalyticsListener = useFabricAnalyticsListenerAdapter();
  const extensions = useMemo(() => {
    const startExtensions = createStartExtensions({
      edition,
      migrationSourceUuid,
    });
    const pageExtensions =
      sourceContext &&
      createProvisioningPageExtensions({
        actions: {
          primary: {
            behaviour: Behaviours.LAND,
            prompt: Prompts.GO_TO_PRODUCT,
            options: {
              targetTab: targetTab || TargetTabs.NEW,
              urlParams: {
                startCrossFlowSrcContext: sourceContext,
                ...(templateId && { templateId }),
              },
            },
          },
          secondary: {
            behaviour: Behaviours.CLOSE,
            prompt: Prompts.CLOSE,
          },
        },
      });
    return { ...startExtensions, ...pageExtensions };
  }, [edition, migrationSourceUuid, sourceContext]);

  /**
   * Mount CrossFlowProvider when `startTrialProduct` param is present.
   *
   * Purposefully not checking for `startTrialCloudId` since it is optional and there are use cases where it is expected to be
   * empty and Cross Flow to be invoked, e.g. for Team Central expansion via Start.
   */
  return startTrialProduct ? (
    <StartCrossFlowProvider
      cloudId={startTrialCloudId}
      analyticsClient={fabricAnalyticsListener}
      locale={intl.locale}
      plugins={[getContainersPlugin]}
    >
      <CrossFlowConsumer
        journey={Journeys.GET_STARTED}
        sourceComponent={sourceComponent || 'redirect'}
        sourceContext={sourceContext || 'start'}
        targetProduct={startTrialProduct}
        experimentalOptions={{ isJsmEnabled: true, ...(billingMode === 'legacy' && { billingMode }) }}
        extensions={extensions}
        onComplete={onComplete}
      />
    </StartCrossFlowProvider>
  ) : null;
};

export default injectIntl(withAnalytics(CrossFlowWAC));
